import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatSelect, MatSelectChange } from "@angular/material/select";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Select, Store } from "@ngxs/store";
import { PermissionsService } from "@zonar-ui/auth";
import { Observable, Subscription, combineLatest } from "rxjs";
import { filter, take } from "rxjs/operators";
import { AppState, GetAssetTypeImage, PatchSettings } from "src/app/app.state";
import { Option } from "src/app/components/multi-select-dropdown/multi-select-dropdown.component";
import { translateAndFormat } from "src/app/i18next";
import { Config } from "src/app/models/config.model";
import { LanguageDictionary } from "src/app/models/language-dictionary.model";
import { Setting } from "src/app/models/setting.model";
import { ZoneLayoutSummary } from "src/app/models/zone-layout-summary.model";
import { LanguageDictionaryService } from "src/app/services/language-dictionary/language-dictionary.service";
import { environment } from "src/environments/environment";
import { isDefined } from "src/utils/isDefined/isDefined";

@Component({
	selector: "app-settings",
	templateUrl: "./settings.component.html",
	styleUrls: ["./settings.component.scss"],
})
export class SettingsComponent implements OnInit, OnDestroy {
	assetTypeImage: SafeHtml;
	assetTypes: Array<Option> = [];
	companyId: string;
	optionsMapValue = {
		[translateAndFormat("1 Year", "title")]: 365,
		[translateAndFormat("2 Years", "title")]: 730,
		[translateAndFormat("3 Years", "title")]: 1095,
		[translateAndFormat("4 Years", "title")]: 1460,
		[translateAndFormat("5 Years", "title")]: 1825,
		[translateAndFormat("indefinitely", "title")]: 1000000000, // 1 billion
	};
	optionsMapRender = {
		365: translateAndFormat("1 year", "title"),
		730: translateAndFormat("2 years", "title"),
		1095: translateAndFormat("3 years", "title"),
		1460: translateAndFormat("4 years", "title"),
		1825: translateAndFormat("5 years", "title"),
		1000000000: translateAndFormat("indefinitely", "title"), // 1 billion
	};
	options: string[] = [
		translateAndFormat("1 year", "title"),
		translateAndFormat("2 years", "title"),
		translateAndFormat("3 years", "title"),
		translateAndFormat("4 years", "title"),
		translateAndFormat("5 years", "title"),
		translateAndFormat("indefinitely", "title"),
	];
	selectedAssetType: string;
	selectedValue: string;
	settings: Setting;
	showImageRetention: boolean;
	subscriptions = new Subscription();

	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;
	@Select(AppState.selectAssetTypeImage) assetTypeImage$: Observable<SafeHtml>;
	@Select(AppState.selectAssetTypes) assetTypes$: Observable<ZoneLayoutSummary[]>;
	@Select(AppState.selectConfig) config$: Observable<Config>;
	@Select(AppState.selectSettings) settings$: Observable<Setting>;
	@Select(AppState.selectLanguageDictionary) languageDictionary$: Observable<LanguageDictionary>;

	@ViewChild("retentionTimeChangeSelect", { static: true }) retentionTimeChangeSelect: MatSelect;
	@ViewChild("radioGroup", { read: ElementRef }) radioGroupRef: ElementRef<HTMLElement>;

	constructor(
		private store: Store,
		private sanitizer: DomSanitizer,
		private permissionsService: PermissionsService,
		private languageDictionaryService: LanguageDictionaryService,
	) {}

	ngOnInit() {
		this.subscriptions.add(
			this.assetTypeImage$.pipe(filter(isDefined)).subscribe((assetTypeImage: string) => {
				const cleanedAssetTypeImage = assetTypeImage
					.replace(/^"(.*)"$/, "$1")
					.replace(/\\"/g, '"')
					.replace(/\\n/g, "");
				this.assetTypeImage = this.sanitizer.bypassSecurityTrustHtml(cleanedAssetTypeImage);
			}),
		);

		this.subscriptions.add(
			combineLatest([
				this.settings$,
				this.assetTypes$,
				this.config$,
				this.selectedCompanyId$,
				this.languageDictionary$,
			])
				.pipe(
					filter(
						([settings, assetTypes, config, selectedCompanyId, languageDictionary]) =>
							isDefined(settings) &&
							isDefined(assetTypes) &&
							isDefined(config) &&
							isDefined(selectedCompanyId) &&
							isDefined(languageDictionary),
					),
				)
				.subscribe(([settings, assetTypes, config, selectedCompanyId]) => {
					this.processStateUpdate(settings, assetTypes, config, selectedCompanyId);
				}),
		);

		this.permissionsService
			.getIsZonarUser()
			.pipe(filter(isDefined), take(1))
			.subscribe(isZonarUser => {
				if (isZonarUser) {
					this.showImageRetention = true;
				}
			});
	}

	processStateUpdate(
		settings: Setting,
		assetTypes: ZoneLayoutSummary[],
		config: Config,
		companyId: string,
	) {
		this.companyId = companyId;
		this.settings = settings;

		// set the controls to whatever the current settings are
		this.selectedValue = this.optionsMapRender[settings.inspectionRetention];

		// Generate the asset types array; prefer translated label from the lang dict, fall back to name
		this.assetTypes = assetTypes.map(({ layoutId, name, langId }) => ({
			value: layoutId,
			label: this.languageDictionaryService.getTranslation(langId) || name,
		}));

		// Set the controls to whatever the current settings are
		const zoneLayoutId = this.assetTypes.find(assetType => assetType.value === settings.defaultZoneLayoutId)?.value;

		this.selectedAssetType = zoneLayoutId;

		const assetViewId = config.zoneLayouts.find(layout => layout.zoneLayoutId.id === zoneLayoutId)?.assetViewId;

		if (assetViewId) {
			this.store.dispatch(
				new GetAssetTypeImage(companyId, assetViewId, environment.environmentConstants.APP_ENDPOINT_EVIR_V2),
			);
		}
	}

	handleRetentionTimeChange(event: MatSelectChange): void {
		this.selectedValue = event.value;

		const payload: Setting = {
			companyId: this.companyId,
			defaultZoneLayoutId: this.settings.defaultZoneLayoutId,
			imageRetention: this.optionsMapValue[event.value],
			inspectionRetention: this.optionsMapValue[event.value],
		};

		this.store.dispatch(new PatchSettings(payload, environment.environmentConstants.APP_ENDPOINT_EVIR_V2));
	}

	handleAssetTypeChange(event): void {
		const payload: Setting = {
			companyId: this.companyId,
			defaultZoneLayoutId: event.value,
		};

		this.store.dispatch(new PatchSettings(payload, environment.environmentConstants.APP_ENDPOINT_EVIR_V2));
	}

	onAssetTypeRadioKeydown(event: KeyboardEvent): void {
		if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

		// Prevent Angular Material's internal FocusKeyManager from handling this —
		// it gets desynced when [value] is updated asynchronously via the store.
		event.preventDefault();
		event.stopPropagation();

		const currentIdx = this.assetTypes.findIndex(t => t.value === this.selectedAssetType);
		const lastIdx = this.assetTypes.length - 1;
		let nextIdx: number;
		if (event.key === "ArrowDown") {
			nextIdx = currentIdx >= lastIdx ? 0 : currentIdx + 1;
		} else {
			nextIdx = currentIdx <= 0 ? lastIdx : currentIdx - 1;
		}

		const nextType = this.assetTypes[nextIdx];
		this.selectedAssetType = nextType.value as string;

		// Focus immediately — trackBy keeps the DOM stable so this won't be stolen
		const inputs = this.radioGroupRef?.nativeElement.querySelectorAll<HTMLElement>('input[type="radio"]');
		inputs?.[nextIdx]?.focus();

		const payload: Setting = {
			companyId: this.companyId,
			defaultZoneLayoutId: nextType.value as string,
		};
		this.store.dispatch(new PatchSettings(payload, environment.environmentConstants.APP_ENDPOINT_EVIR_V2));
	}

	trackAssetTypeValue(_index: number, item: Option): string {
		return item.value as string;
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}
}
